import { Button, Switch, TextField, useAlert } from '@ads/components-react';
import { zodResolver } from '@hookform/resolvers/zod';
import * as Dialog from '@radix-ui/react-dialog';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { CountryViewModel } from '@/shared/api/generated';

import { useSaveCountry } from '../../../hooks/useSaveCountry';
import { type CountryFormValues, countryFormSchema } from '../../../types/countryForm';

export interface CountryFormModalProps {
  readonly open: boolean;
  /** País em edição; `null` abre o modal em modo de criação. */
  readonly country: CountryViewModel | null;
  readonly onOpenChange: (open: boolean) => void;
}

const DEFAULT_VALUES: CountryFormValues = { code: '', name: '', active: true };

/**
 * Modal de criação/edição de País (Radix Dialog + React Hook Form + Zod).
 *
 * Um único formulário cobre criar (campos vazios, Ativo ligado) e editar
 * (pré-preenchido a partir da linha). Persiste via `useSaveCountry` e mapeia o
 * conflito **409** (sigla/nome já cadastrados) para erro nos campos, mantendo o
 * modal aberto. Fiel aos frames *Modal — Adicionar/Editar país* de
 * `design/location.pen`.
 */
export function CountryFormModal({ open, country, onOpenChange }: CountryFormModalProps) {
  const { t } = useTranslation('location');
  const { alert } = useAlert();
  const { save, isPending } = useSaveCountry();
  const [hasConflict, setHasConflict] = useState(false);

  const isEdit = country?.id != null;

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CountryFormValues>({
    resolver: zodResolver(countryFormSchema),
    defaultValues: DEFAULT_VALUES,
  });

  // Sincroniza o formulário sempre que o modal abre (com o país em edição ou vazio).
  useEffect(() => {
    if (!open) return;
    setHasConflict(false);
    reset(
      country
        ? { code: country.code ?? '', name: country.name ?? '', active: country.active ?? true }
        : DEFAULT_VALUES,
    );
  }, [open, country, reset]);

  const onSubmit = handleSubmit(values => {
    setHasConflict(false);
    save(values, country?.id ?? undefined, {
      onSuccess: () => {
        alert({
          alertType: 'success',
          title: t(isEdit ? 'countries.toast.updateSuccess' : 'countries.toast.createSuccess'),
        });
        onOpenChange(false);
      },
      onConflict: () => setHasConflict(true),
      onError: () => alert({ alertType: 'warning', title: t('countries.toast.saveError') }),
    });
  });

  const codeHelper = errors.code
    ? t('countries.form.errors.codeRequired')
    : hasConflict
      ? t('countries.form.errors.conflict')
      : undefined;
  const nameHelper = errors.name
    ? t('countries.form.errors.nameRequired')
    : hasConflict
      ? t('countries.form.errors.conflict')
      : undefined;

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-none z-50 h-screen w-screen bg-base-gray-900 bg-opacity-medium backdrop-blur-soft" />
        <Dialog.Content
          aria-describedby={undefined}
          className="-translate-x-1/2 -translate-y-1/2 fixed top-1/2 left-1/2 z-50 flex w-full max-w-[32rem] flex-col gap-xxs rounded-lg bg-ctx-layout-surface p-xxs shadow-level4 focus:outline-none"
        >
          <Dialog.Title className="text-ctx-content-title ts-heading-xs">
            {t(isEdit ? 'countries.form.editTitle' : 'countries.form.createTitle')}
          </Dialog.Title>

          <form className="flex flex-col gap-mini" onSubmit={onSubmit} noValidate>
            <div className="grid grid-cols-1 gap-mini sm:grid-cols-[150px_1fr]">
              <Controller
                control={control}
                name="code"
                render={({ field }) => (
                  <TextField
                    label={`${t('countries.form.code')}*`}
                    placeholder={t('countries.form.codePlaceholder')}
                    value={field.value}
                    onChange={e => {
                      setHasConflict(false);
                      field.onChange(e.target.value);
                    }}
                    size="md"
                    fullWidth
                    hasError={Boolean(errors.code) || hasConflict}
                    {...(codeHelper ? { helper: codeHelper } : {})}
                  />
                )}
              />

              <Controller
                control={control}
                name="name"
                render={({ field }) => (
                  <TextField
                    label={`${t('countries.form.name')}*`}
                    placeholder={t('countries.form.namePlaceholder')}
                    value={field.value}
                    onChange={e => {
                      setHasConflict(false);
                      field.onChange(e.target.value);
                    }}
                    size="md"
                    fullWidth
                    hasError={Boolean(errors.name) || hasConflict}
                    {...(nameHelper ? { helper: nameHelper } : {})}
                  />
                )}
              />
            </div>

            <Controller
              control={control}
              name="active"
              render={({ field }) => (
                <Switch
                  checked={field.value}
                  size="md"
                  label={t('countries.form.active')}
                  onCheckedChange={checked => field.onChange(checked)}
                />
              )}
            />

            <div className="mt-nano flex justify-end gap-nano">
              <Dialog.Close asChild>
                <Button hierarchy="secondary" size="md" type="button">
                  {t('countries.form.cancel')}
                </Button>
              </Dialog.Close>
              <Button
                hierarchy="primary"
                size="md"
                type="submit"
                isLoading={isPending}
                disabled={isPending}
              >
                {t('countries.form.save')}
              </Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
